export const ANNOUNCEMENT_TYPE_OPTIONS = [
  { value: "info", label: "Info" },
  { value: "success", label: "Success" },
  { value: "warning", label: "Warning" },
  { value: "error", label: "Error" },
  { value: "maintenance", label: "Maintenance" },
];

export const ANNOUNCEMENT_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "expired", label: "Expired" },
];

export const ANNOUNCEMENT_DISPLAY_MODE_OPTIONS = [
  { value: "popup", label: "Popup" },
];

export const DEFAULT_ROLE_OPTIONS = [
  "admin",
  "manager",
  "user",
  "finance",
].map((value) => ({
  value,
  label: value,
}));

export const KNOWN_ANNOUNCEMENT_PAGES = [
  "/",
  "/quotations",
  "/quotations/create",
  "/approvals",
  "/approve-flow",
  "/manage-flows",
  "/repair-approval-flow",
  "/users",
  "/clients",
  "/logs",
  "/profile",
  "/meeting-rooms",
  "/travel-expense",
  "/travel-expense/request",
  "/travel-expense/approve",
  "/travel-expense/logs",
].map((value) => ({
  value,
  label: value,
}));

export const createEmptyAnnouncementForm = () => ({
  title: "",
  message: "",
  type: "info",
  status: "draft",
  priority: 0,
  displayMode: "popup",
  audience: {
    showToAll: true,
    roles: [],
    departments: [],
    userIds: [],
  },
  pages: [],
  startAt: "",
  endAt: "",
  requiresAcknowledgement: false,
  dismissible: true,
  showOncePerUser: false,
  cta: {
    label: "",
    url: "",
  },
});

export const mapAnnouncementToFormValues = (announcement = {}) => ({
  title: announcement.title || "",
  message: announcement.message || "",
  type: announcement.type || "info",
  status: announcement.status || "draft",
  priority:
    announcement.priority == null || Number.isNaN(Number(announcement.priority))
      ? 0
      : Number(announcement.priority),
  displayMode: announcement.displayMode || "popup",
  audience: {
    showToAll: Boolean(announcement.audience?.showToAll),
    roles: Array.isArray(announcement.audience?.roles)
      ? announcement.audience.roles
      : [],
    departments: Array.isArray(announcement.audience?.departments)
      ? announcement.audience.departments
      : [],
    userIds: Array.isArray(announcement.audience?.userIds)
      ? announcement.audience.userIds
      : [],
  },
  pages: Array.isArray(announcement.pages) ? announcement.pages : [],
  startAt: toDateTimeLocalValue(announcement.startAt),
  endAt: toDateTimeLocalValue(announcement.endAt),
  requiresAcknowledgement: Boolean(announcement.requiresAcknowledgement),
  dismissible:
    announcement.dismissible == null ? true : Boolean(announcement.dismissible),
  showOncePerUser: Boolean(announcement.showOncePerUser),
  cta: {
    label: announcement.cta?.label || "",
    url: announcement.cta?.url || "",
  },
});

export const buildAnnouncementPayload = (formValues) => ({
  title: String(formValues.title || "").trim(),
  message: String(formValues.message || "").trim(),
  type: formValues.type || "info",
  status: formValues.status || "draft",
  priority: Number(formValues.priority || 0),
  displayMode: formValues.displayMode || "popup",
  audience: {
    showToAll: Boolean(formValues.audience?.showToAll),
    roles: sanitizeStringArray(formValues.audience?.roles),
    departments: sanitizeStringArray(formValues.audience?.departments),
    userIds: sanitizeStringArray(formValues.audience?.userIds),
  },
  pages: sanitizeStringArray(formValues.pages),
  startAt: formValues.startAt ? new Date(formValues.startAt).toISOString() : null,
  endAt: formValues.endAt ? new Date(formValues.endAt).toISOString() : null,
  requiresAcknowledgement: Boolean(formValues.requiresAcknowledgement),
  dismissible: Boolean(formValues.dismissible),
  showOncePerUser: Boolean(formValues.showOncePerUser),
  cta:
    String(formValues.cta?.label || "").trim() || String(formValues.cta?.url || "").trim()
      ? {
          label: String(formValues.cta?.label || "").trim(),
          url: String(formValues.cta?.url || "").trim(),
        }
      : null,
});

export const validateAnnouncementForm = (formValues) => {
  if (!String(formValues.title || "").trim()) {
    return "กรุณากรอกหัวข้อประกาศ";
  }

  if (!String(formValues.message || "").trim()) {
    return "กรุณากรอกข้อความประกาศ";
  }

  if (formValues.startAt && formValues.endAt) {
    const start = new Date(formValues.startAt).getTime();
    const end = new Date(formValues.endAt).getTime();

    if (!Number.isNaN(start) && !Number.isNaN(end) && end < start) {
      return "เวลาสิ้นสุดต้องไม่น้อยกว่าเวลาเริ่มต้น";
    }
  }

  if (
    (String(formValues.cta?.label || "").trim() &&
      !String(formValues.cta?.url || "").trim()) ||
    (!String(formValues.cta?.label || "").trim() &&
      String(formValues.cta?.url || "").trim())
  ) {
    return "หากต้องการ CTA กรุณากรอกทั้งข้อความปุ่มและลิงก์";
  }

  return "";
};

export const formatAnnouncementDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("th-TH", {
    dateStyle: "short",
    timeStyle: "short",
  });
};

export const toSelectOptions = (values = []) =>
  sanitizeStringArray(values).map((value) => ({
    value,
    label: value,
  }));

export const getAnnouncementTypeClasses = (type = "info") => {
  const normalized = String(type).toLowerCase();

  if (normalized === "success") {
    return {
      accent: "bg-emerald-100 text-emerald-700 border-emerald-200",
      panel: "border-emerald-200 bg-emerald-50/80",
      button: "bg-emerald-600 hover:bg-emerald-700 text-white",
    };
  }

  if (normalized === "warning") {
    return {
      accent: "bg-amber-100 text-amber-700 border-amber-200",
      panel: "border-amber-200 bg-amber-50/80",
      button: "bg-amber-500 hover:bg-amber-600 text-white",
    };
  }

  if (normalized === "error") {
    return {
      accent: "bg-rose-100 text-rose-700 border-rose-200",
      panel: "border-rose-200 bg-rose-50/80",
      button: "bg-rose-600 hover:bg-rose-700 text-white",
    };
  }

  if (normalized === "maintenance") {
    return {
      accent: "bg-sky-100 text-sky-700 border-sky-200",
      panel: "border-sky-200 bg-sky-50/80",
      button: "bg-sky-600 hover:bg-sky-700 text-white",
    };
  }

  return {
    accent: "bg-slate-100 text-slate-700 border-slate-200",
    panel: "border-slate-200 bg-white",
    button: "bg-slate-700 hover:bg-slate-800 text-white",
  };
};

export const getAnnouncementStatusClasses = (status = "draft") => {
  const normalized = String(status).toLowerCase();

  if (normalized === "active") {
    return "bg-emerald-100 text-emerald-700";
  }

  if (normalized === "inactive") {
    return "bg-amber-100 text-amber-700";
  }

  if (normalized === "expired") {
    return "bg-rose-100 text-rose-700";
  }

  return "bg-slate-100 text-slate-700";
};

export const isInternalAnnouncementUrl = (url = "") =>
  String(url || "").startsWith("/");

export const getUserOptionLabel = (user = {}) => {
  const labelParts = [user.firstName, user.lastName].filter(Boolean);
  const nameLabel = labelParts.join(" ").trim();
  const nickname = user.nickname ? ` (${user.nickname})` : "";
  const username = user.username || user.email || user._id || "";

  return nameLabel ? `${nameLabel}${nickname} - ${username}` : username;
};

const toDateTimeLocalValue = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const timezoneOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
};

const sanitizeStringArray = (values = []) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );
